// MFA enrollment page (2026-06-21).
//
// Reached ONLY by a deliberate user action — there is no
// redirect-to-enroll, no app-blocking-until-enrolled, no auto-prompt
// on login. Opt-in by design per MFA Chunk 4.
//
// Flow:
//   1. On mount → POST /auth/mfa/enroll/start.
//      Shows QR + manual key. User scans with their app.
//   2. User types the 6-digit code → POST /auth/mfa/enroll/verify.
//      Server returns the 10 plaintext recovery codes ONCE.
//   3. Codes shown with copy/download + a required "I've saved my
//      recovery codes" checkbox before the user can leave. After
//      they acknowledge, the only way "off" the page is to navigate
//      away (we don't auto-redirect).
//
// We never persist the QR / secret / codes anywhere — they live in
// component state only and are GC'd when the user leaves the page.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import {
  Shield, Loader2, AlertCircle, Copy, Check, Download, KeyRound, ArrowLeft,
} from "lucide-react";

// Extract the otpauth `secret=…` so we can display the manual setup
// key (for users whose phone can't scan QR).
const extractSecret = (uri) => {
  try {
    const m = uri.match(/[?&]secret=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
};

export default function MfaEnroll() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // "loading" | "enroll" | "verifying" | "done" | "error"
  const [enrollData, setEnrollData] = useState(null); // {provisioning_uri, qr_image_base64}
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [savedAck, setSavedAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/auth/mfa/enroll/start");
        if (!cancelled) {
          setEnrollData(res.data);
          setPhase("enroll");
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || "Failed to start MFA enrollment.";
        setError(detail);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setPhase("verifying");
    try {
      const res = await api.post("/auth/mfa/enroll/verify", { code: code.trim() });
      setRecoveryCodes(res.data.recovery_codes || []);
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid code. Try again.");
      setPhase("enroll");
    }
  };

  const copyCodes = async () => {
    if (!recoveryCodes) return;
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch { /* clipboard blocked — user can still read codes */ }
  };

  const downloadCodes = () => {
    if (!recoveryCodes) return;
    const blob = new Blob(
      [`WestStar Portal — MFA recovery codes\nGenerated: ${new Date().toISOString()}\n\n${recoveryCodes.join("\n")}\n`],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "weststar-mfa-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Loading ──
  if (phase === "loading") {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="mfa-enroll-loading">
        <Loader2 size={28} className="animate-spin text-blue-600" />
        <p className="mt-3 text-sm text-slate-500">Starting MFA enrollment…</p>
      </div>
    );
  }

  // ── Hard error (start failed) ──
  if (phase === "error") {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="mfa-enroll-error">
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>
    );
  }

  // ── Success: show recovery codes ──
  if (phase === "done") {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="mfa-enroll-done">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Check size={20} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              MFA is now enabled
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              From your next login you&apos;ll be asked for a code from your authenticator app.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 mb-5">
          <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold mb-1">
            <AlertCircle size={15} /> Save these recovery codes now
          </div>
          <p className="text-xs text-amber-700 mb-4">
            They will <strong>not</strong> be shown again. Each code is single-use and lets you
            sign in if you lose access to your authenticator app.
          </p>
          <ul
            data-testid="mfa-recovery-codes"
            className="grid grid-cols-2 gap-2 mb-4 font-mono text-sm bg-white rounded-lg border border-amber-200 p-4"
          >
            {recoveryCodes.map((c) => (
              <li key={c} className="text-slate-900 tracking-wider" data-testid={`mfa-recovery-${c}`}>{c}</li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyCodes}
              data-testid="mfa-recovery-copy"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
            >
              {copiedAll ? <Check size={13} /> : <Copy size={13} />}
              {copiedAll ? "Copied!" : "Copy all"}
            </button>
            <button
              type="button"
              onClick={downloadCodes}
              data-testid="mfa-recovery-download"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
            >
              <Download size={13} /> Download as .txt
            </button>
          </div>
        </div>

        <label className="flex items-start gap-2 mb-5 cursor-pointer">
          <input
            type="checkbox"
            data-testid="mfa-recovery-acknowledge"
            checked={savedAck}
            onChange={(e) => setSavedAck(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-slate-700">
            I&apos;ve saved my recovery codes somewhere safe.
          </span>
        </label>

        <button
          type="button"
          disabled={!savedAck}
          onClick={() => navigate(-1)}
          data-testid="mfa-recovery-done"
          className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
        >
          Done
        </button>
      </div>
    );
  }

  // ── Enroll: show QR + code input ──
  const secret = enrollData ? extractSecret(enrollData.provisioning_uri) : "";
  return (
    <div className="max-w-2xl mx-auto p-8" data-testid="mfa-enroll-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <Shield size={20} className="text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Set up two-factor authentication
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Scan the QR code with Google Authenticator, Authy, 1Password, or any TOTP app.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col items-center">
          {enrollData?.qr_image_base64 ? (
            <img
              src={`data:image/png;base64,${enrollData.qr_image_base64}`}
              alt="MFA QR code"
              className="w-48 h-48"
              data-testid="mfa-enroll-qr"
            />
          ) : <Loader2 size={28} className="animate-spin text-slate-400" />}
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 inline-flex items-center gap-1.5">
            <KeyRound size={13} /> Or enter this key manually
          </p>
          <code
            data-testid="mfa-enroll-secret"
            className="block break-all text-sm font-mono bg-white border border-slate-200 rounded-md p-2 mb-3"
          >{secret}</code>
          <p className="text-xs text-slate-500">
            Use this if your phone can&apos;t scan the QR code.
          </p>
        </div>
      </div>

      {error && (
        <div data-testid="mfa-enroll-form-error" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-5">
          <AlertCircle size={15} /> {error}
        </div>
      )}

      <form onSubmit={handleVerify} className="flex items-end gap-3" data-testid="mfa-enroll-form">
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Enter the 6-digit code shown by your app
          </label>
          <input
            data-testid="mfa-enroll-code"
            type="text"
            autoFocus
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm tracking-widest text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="123456"
            required
          />
        </div>
        <button
          type="submit"
          data-testid="mfa-enroll-verify"
          disabled={phase === "verifying" || code.length !== 6}
          className="h-11 px-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {phase === "verifying" ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : "Verify"}
        </button>
      </form>

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft size={14} /> Cancel
      </button>
    </div>
  );
}
